import { AssetType, ContentType, ErrorCode } from "@thenounproject/lingo-core";

import { type Upload } from "@actions/uploads";
import { fontFileExtensions } from "@constants/fontFileExtensions";
import { getFileName, typeErrorMessage } from "@helpers/files";
import formatBytes from "@helpers/bytesToSize";
import { type ExternalSource } from "../items/useCreateAssetsFromSource";

type AssetPayload = {
  type?: string;
  source_url?: string;
  name?: string;
  meta: {
    font?: { font_name?: string; extension?: string };
  };
};

type ProcessExternalFileOptions = {
  setAssetName?: boolean;
};

export function processExternalFile(
  source: ExternalSource,
  upload: Upload,
  asset: AssetPayload,
  contentTypes: ContentType[],
  options: ProcessExternalFileOptions = {}
) {
  const { setAssetName = false } = options;

  upload.name = source.name ?? "Unknown file";
  asset.source_url = source.url;
  const { name, extension: fileExtension } = getFileName(source.name);

  if (fontFileExtensions.includes(fileExtension)) {
    asset.type = AssetType.textStyle;
    asset.meta = { font: { extension: fileExtension } };
    return;
  }

  const type = contentTypes.find(
    t => t.fileExtension.toLowerCase() === fileExtension || t.aliases.includes(fileExtension)
  );

  if (!type) {
    // We couldn't determine the file type
    upload.status = "failed";
    upload.error = {
      code: ErrorCode.invalidParams,
      message: typeErrorMessage(contentTypes),
    };
  } else if (type.maxSize && source.fileSize != null && source.fileSize > type.maxSize) {
    // File too large
    upload.status = "failed";
    upload.error = {
      code: ErrorCode.fileTooLarge,
      message: `The file must be smaller than ${formatBytes(type.maxSize)}.`,
    };
  } else if (type.assetType === AssetType.textStyle) {
    asset.type = AssetType.textStyle;
    asset.meta = { font: { extension: fileExtension } };
  } else {
    asset.type = type.assetType;
    if (setAssetName) {
      asset.name = name;
    }
  }
}
